import { isEnrichmentContentType } from "@/lib/tina/content-types";

/**
 * Resolving a Mood entity back to the Tina item that produced it.
 *
 * **Two mechanisms, and they are not interchangeable.** Tina leaves two
 * different pointers on an entity, answering two different questions:
 *
 * | pointer | written by | answers | stable? |
 * |---|---|---|---|
 * | `metadata` stamp | the create payload | *which walk created this?* | yes — never repointed |
 * | `enrichment.taskName` | every enrichment write-back | *what last enriched this?* | **no — it moves** |
 *
 * `taskName` is `tina:{content_type}:{job_event_id}`, and it is rewritten every
 * time the entity is enriched again. Event `02sJKkVW` in prod is the worked
 * example: `metadata.id = 236b1183` (the walk that created it) *and*
 * `taskName = tina:enrichment_event:b21f2aa1` (the repair job that later
 * re-enriched it) — two different, both-correct answers.
 *
 * So {@link resolveStaticTracePointer} reads them **in that order**. Reading
 * `taskName` first silently downgrades a Tina-created entity's trace to
 * whichever job last touched it, and for venues/movies/people that is
 * unrecoverable: the `metadata` stamp is their only creation pointer. (Events
 * keep `metadata.id` regardless, which is exactly why the bug is invisible if
 * you only test on events.)
 *
 * The third tier — `GET /events?entity_id=` — is a live lookup rather than a
 * pointer, so it lives in `use-entity-trace.ts`.
 *
 * **Genre has no trace by design.** Genre enrichment persists flat on the genre
 * document, which has no `taskName` field, so `UpdateGenreEnrichmentCommand`
 * accepts the value and drops it. Genre pages render no trace.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** How a trace pointer was found — surfaced so the card can label itself. */
export type TraceSource = "metadata_stamp" | "task_name" | "entity_lookup";

export interface TracePointer {
  /** The Tina `events` row id — an item id, whatever tier produced it. */
  itemId: string;
  source: TraceSource;
  /** Only the `metadata` stamp carries these; otherwise read off the item. */
  runId?: string;
  site?: string;
  url?: string;
  /** Only `task_name` carries this without a fetch. */
  contentType?: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Tier 1 — the creation stamp on `metadata`.
 *
 * Two spellings are accepted because the collision question was left open: the
 * event block's shape is fixed (`{ id, runId, site, url, source: "tina" }`),
 * but venues already carry `googleMapsCid` and artists carry
 * `source: "spotify"`, so the stamp may land nested under `metadata.tina`
 * instead. Reading both means the IMS half does not have to ship in lockstep
 * with whichever Tina picks.
 *
 * `id` is validated as a UUID rather than trusted: `metadata.id` on a
 * non-Tina-created entity could be any provider's identifier, and rendering a
 * trace card for it would 404 loudly on a page that was fine.
 */
export function readTinaStamp(metadata: unknown): TracePointer | null {
  const root = asRecord(metadata);
  if (!root) return null;

  const nested = asRecord(root.tina);
  const block = nested ?? root;

  const id = asString(block.id);
  if (!id || !UUID_RE.test(id)) return null;

  return {
    itemId: id,
    source: "metadata_stamp",
    runId: asString(block.runId) ?? asString(block.jobId),
    site: asString(block.site),
    url: asString(block.url),
  };
}

/**
 * Tier 2 — `enrichment.taskName`, `tina:{content_type}:{job_event_id}`.
 *
 * The embedded content type tells the caller what kind of item the uuid names
 * (a `music_event` walk vs an `enrichment_venue` job) with no extra lookup, so
 * the card can label itself before the fetch resolves.
 */
export function parseTaskName(
  taskName: string | null | undefined,
): TracePointer | null {
  if (!taskName) return null;
  const parts = taskName.split(":");
  if (parts.length !== 3) return null;
  const [prefix, contentType, itemId] = parts;
  if (prefix !== "tina" || !contentType || !UUID_RE.test(itemId)) return null;
  return { itemId, source: "task_name", contentType };
}

/**
 * Tiers 1 → 2, in the only order that is correct. Returns null when the entity
 * carries neither pointer, which is when the `entity_id` lookup earns its
 * keep — every entity created before the `metadata` stamp shipped is in that
 * bucket, and so is every artist/venue whose `taskName` was destroyed by the
 * `.passthrough()` data loss since fixed (already-destroyed pointers are not
 * recoverable).
 */
export function resolveStaticTracePointer(input: {
  metadata?: unknown;
  taskName?: string | null;
}): TracePointer | null {
  return readTinaStamp(input.metadata) ?? parseTaskName(input.taskName);
}

/**
 * `enrichment.taskName` off a loosely-typed enrichment object.
 *
 * Every read schema in IMS is `.passthrough()`, which types unmodelled keys as
 * `unknown` — so every caller would otherwise write the same cast. Centralising
 * it also means a caller cannot accidentally pass the enrichment object where a
 * task name is expected.
 */
export function readTaskName(enrichment: unknown): string | undefined {
  return asString(asRecord(enrichment)?.taskName);
}

/**
 * The `entity_id` an enrichment job row was queued for, off its
 * `metadata.enrichment` block.
 *
 * This is the guard that makes the lookup safe to ship before the Tina
 * deploy that serves it: FastAPI **ignores** an unknown query parameter rather
 * than rejecting it, so an orchestrator without `entity_id` answers the same
 * request with an unfiltered page of somebody else's jobs. Verifying the id
 * client-side turns that from "wrong trace" into "no trace".
 */
export function readJobEntityId(metadata: unknown): string | undefined {
  const enrichment = asRecord(asRecord(metadata)?.enrichment);
  return asString(enrichment?.entity_id);
}

/** True when a job row genuinely belongs to this entity. See {@link readJobEntityId}. */
export function jobMatchesEntity(
  item: { content_type: string; metadata: Record<string, unknown> | null },
  entityId: string,
): boolean {
  return (
    isEnrichmentContentType(item.content_type) &&
    readJobEntityId(item.metadata) === entityId
  );
}
